/* ==========================================================================
   InstaSeal Hybrid — landing/PDP behaviour
   - Gallery carousel (thumbs, prev/next, counter)
   - Kit tabs -> price / bag counts / variant id (kit N = variant N)
   - Quantity stepper
   - Add to cart through Horizon's cart drawer events (falls back to /cart)
   - "What's in the box" tabs (synced with the buy box)
   - Sticky add-to-cart bar
   ========================================================================== */

const qs = (sel, root = document) => root.querySelector(sel);
const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

/* Lifecycle: every global listener / observer created during boot() is tied to one AbortController,
   so the theme editor can re-run boot() after it re-renders a section without double-binding. */
let LIFE = new AbortController();
const OBSERVERS = [];
const listen = (target, type, fn, opts) => {
  const o = typeof opts === 'object' && opts !== null ? opts : { capture: !!opts };
  target.addEventListener(type, fn, { ...o, signal: LIFE.signal });
};
const observe = (observer) => {
  OBSERVERS.push(observer);
  return observer;
};

/* ---------- Cart helpers ---------- */

/**
 * Horizon's own "fly to cart" animation (assets/fly-to-cart.js, styles in snippets/fly-to-cart-styles).
 * @param {{ source: Element, image?: string, variant?: 'main'|'quick'|'sticky', useSourceSize?: boolean }} opts
 */
function flyToCart({ source, image, variant = 'main', useSourceSize = false }) {
  const cartIcon = qs('.header-actions__cart-icon');
  if (!cartIcon || !source || !image || !customElements.get('fly-to-cart')) return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  const el = document.createElement('fly-to-cart');
  el.classList.add(`fly-to-cart--${variant}`);
  el.style.setProperty('background-image', `url(${image})`);
  el.style.setProperty('--start-opacity', '0');
  el.useSourceSize = useSourceSize;
  el.source = source;
  el.destination = cartIcon;
  document.body.appendChild(el);
}

async function addToCart({ items, source, fly }) {
  const cartUrl = window.Theme?.routes?.cart_url || '/cart';
  const addUrl = `${window.Theme?.routes?.cart_add_url || '/cart/add'}.js`;

  // Load Horizon's event classes lazily so a CDN hiccup can never break the rest of the page.
  let events = null;
  try {
    events = await import('@shopify/events');
  } catch (e) {
    events = null;
  }

  const sectionIds = qsa('cart-items-component')
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const first = items[0];
  let deferred = null;
  if (events?.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    // Dispatch before the request, like Horizon's own product form, so the drawer opens immediately.
    source.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
  }

  try {
    const res = await fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    });
    const json = await res.json();

    if (!res.ok || json.status) {
      throw Object.assign(new Error(json.description || json.message || 'Could not add to cart'), { userFacing: true });
    }

    if (!events?.CartLinesUpdateEvent) {
      // No drawer events available: send the shopper to the cart page.
      window.location.href = cartUrl;
      return json;
    }

    if (fly) flyToCart(fly);

    const cartRes = await fetch(`${cartUrl}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const ajaxCart = await cartRes.json();

    deferred.resolve({
      cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'product-form-component',
        sourceId: 'is-buy-box',
        itemCount: items.reduce((n, i) => n + i.quantity, 0),
        productId: first?.productId,
        sections: json.sections,
        didError: false,
      },
    });
    return json;
  } catch (error) {
    deferred?.reject?.(error);
    if (events?.CartErrorEvent) {
      source.dispatchEvent(
        new events.CartErrorEvent({
          error: error.message || 'Add to cart failed',
          code: 'INVALID',
        })
      );
    }
    throw error;
  }
}

/* ---------- Buy box ---------- */

function initBuyBox(root) {
  const tabs = qsa('[data-is-tab]', root);
  const form = qs('[data-is-form]', root);
  if (!form) return null;

  const variantInput = qs('[data-is-variant]', form);
  const qtyInput = qs('[data-is-qty-input]', form);
  const qtyOut = qs('[data-is-qty-out]', form);
  const atcBtn = qs('[data-is-atc-btn]', form);
  const atcLabel = qs('[data-is-atc-label]', form);
  const atcPriceWrap = qs('[data-is-atc-price-wrap]', form);
  const atcPrice = qs('[data-is-atc-price]', form);
  const errorEl = qs('[data-is-error]', form);
  const dot = qs('[data-is-dot]', root);
  const priceEls = qsa('[data-is-price]', root);
  const compareWrap = qs('[data-is-compare-wrap]', root);
  const compareEl = qs('[data-is-compare]', root);
  const saveEl = qs('[data-is-save]', root);
  const bagsEls = qsa('[data-is-bags]', root);
  const mEl = qs('[data-is-m]', root);
  const lEl = qs('[data-is-l]', root);
  const originalLabel = atcLabel?.textContent || 'Add to Cart';

  const state = { tier: 0, qty: 1 };

  const setTier = (i, { silent = false } = {}) => {
    if (!tabs.length) return;
    state.tier = Math.max(0, Math.min(tabs.length - 1, i));
    tabs.forEach((t, idx) => t.setAttribute('aria-selected', idx === state.tier ? 'true' : 'false'));
    const t = tabs[state.tier];
    const d = t.dataset;

    priceEls.forEach((el) => (el.textContent = d.price));
    if (atcPrice) atcPrice.textContent = d.price;
    qsa('[data-is-kit-img]', root).forEach((w) => w.classList.toggle('is-active', w.dataset.isKitImg === String(state.tier)));
    bagsEls.forEach((el) => (el.textContent = d.bags));
    if (mEl) mEl.textContent = d.m;
    if (lEl) lEl.textContent = d.l;

    const hasCompare = !!d.compare;
    if (compareWrap) compareWrap.hidden = !hasCompare;
    if (compareEl) compareEl.textContent = d.compare || '';
    if (saveEl) {
      saveEl.hidden = !d.save;
      saveEl.textContent = d.save || '';
    }

    const placeholder = d.placeholder === 'true';
    const available = d.available === 'true';
    if (variantInput && d.variantId) variantInput.value = d.variantId;

    if (atcBtn) {
      const disabled = placeholder || !available;
      atcBtn.disabled = disabled;
      atcBtn.toggleAttribute('aria-disabled', disabled);
    }
    if (atcLabel) {
      atcLabel.textContent = placeholder ? 'Coming soon' : !available ? 'Sold out' : originalLabel;
    }
    if (atcPriceWrap) atcPriceWrap.hidden = placeholder || !available;
    if (dot) dot.classList.toggle('is-ship__dot--out', !placeholder && !available);
    if (errorEl) errorEl.hidden = true;

    if (!silent) {
      document.dispatchEvent(
        new CustomEvent('is:tier', {
          detail: { index: state.tier, bags: d.bags, price: d.price, m: d.m, l: d.l, placeholder, available, mediaIndex: d.mediaIndex },
        })
      );
    }
  };

  const setQty = (n) => {
    state.qty = Math.max(1, Math.min(9, n));
    if (qtyInput) qtyInput.value = String(state.qty);
    if (qtyOut) qtyOut.textContent = String(state.qty);
  };

  tabs.forEach((t, i) => t.addEventListener('click', () => setTier(i)));

  // Reverse sync: browsing the gallery onto a variant's own image selects that variant.
  listen(document, 'is:slide', (e) => {
    const mi = String(e.detail?.index);
    const owners = tabs.filter((t) => t.dataset.mediaIndex === mi && t.dataset.placeholder !== 'true');
    if (owners.length !== 1) return;
    const i = tabs.indexOf(owners[0]);
    if (i !== -1 && i !== state.tier) setTier(i);
  });
  qs('[data-is-dec]', form)?.addEventListener('click', () => setQty(state.qty - 1));
  qs('[data-is-inc]', form)?.addEventListener('click', () => setQty(state.qty + 1));

  const submit = async (event, flyFrom = null) => {
    event?.preventDefault?.();
    if (atcBtn?.disabled) return;
    if (errorEl) errorEl.hidden = true;
    atcBtn.disabled = true;
    const image = tabs[state.tier]?.dataset.image || root.dataset.image || '';
    try {
      await addToCart({
        source: atcBtn,
        fly: flyFrom
          ? { source: flyFrom, image: flyFrom.currentSrc || flyFrom.src || image, variant: 'sticky', useSourceSize: true }
          : { source: atcBtn, image, variant: 'main' },
        items: [{ id: Number(variantInput.value), quantity: state.qty, productId: root.dataset.productId }],
      });
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error.message || 'Something went wrong. Please try again.';
        errorEl.hidden = false;
      }
    } finally {
      // Re-evaluate disabled state from the selected tab.
      setTier(state.tier, { silent: true });
    }
  };
  form.addEventListener('submit', submit);

  const startKit = parseInt(root.dataset.defaultKit || '0', 10);
  // Prefer the configured default kit when it maps to a real variant; otherwise first real variant.
  let initial = Number.isNaN(startKit) ? 0 : startKit;
  if (tabs[initial]?.dataset.placeholder === 'true') {
    const firstReal = tabs.findIndex((t) => t.dataset.placeholder !== 'true');
    initial = firstReal >= 0 ? firstReal : 0;
  }
  setTier(initial);
  setQty(1);

  initGallery(root);

  return { submit, atcBtn, state, setTier };
}

/* ---------- Gallery ---------- */

function initGallery(root) {
  const gallery = qs('[data-is-gallery]', root);
  if (!gallery) return;
  const slides = qsa('[data-is-slide]', gallery);
  const thumbs = qsa('[data-is-thumb]', gallery);
  const count = qs('[data-is-count]', gallery);
  let index = 0;

  const show = (i) => {
    if (!slides.length) return;
    index = (i + slides.length) % slides.length;
    slides.forEach((s, idx) => s.classList.toggle('is-active', idx === index));
    thumbs.forEach((t, idx) => t.setAttribute('aria-pressed', idx === index ? 'true' : 'false'));
    if (count) count.textContent = `${index + 1} / ${slides.length}`;
    syncVideo();
    document.dispatchEvent(new CustomEvent('is:slide', { detail: { index } }));
  };

  // Video slides: autoplay (muted, looping) while active, pause + rewind when not.
  // A manual pause is remembered per video; reduced-motion users get no autoplay.
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const paused = new WeakSet();
  const setCtl = (slide, isPaused) => {
    const btn = qs('[data-is-vctl]', slide);
    if (!btn) return;
    btn.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
    btn.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
  };
  const syncVideo = () => {
    slides.forEach((s, idx) => {
      const v = qs('video', s);
      if (!v) return;
      if (idx === index) {
        if (reduceMotion && !paused.has(v) && !v.dataset.isTouched) paused.add(v);
        if (!paused.has(v)) v.play?.().catch(() => {});
        setCtl(s, paused.has(v) || v.paused);
      } else {
        v.pause();
        try { v.currentTime = 0; } catch (e) { /* metadata not loaded yet */ }
      }
    });
  };
  slides.forEach((s) => {
    const v = qs('video', s);
    const btn = qs('[data-is-vctl]', s);
    if (!v || !btn) return;
    // Videos must be muted to autoplay; keep attributes explicit in case a theme-editor re-render drops them.
    v.muted = true; v.loop = true; v.playsInline = true;
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      v.dataset.isTouched = '1';
      if (v.paused) { paused.delete(v); v.play().catch(() => {}); setCtl(s, false); }
      else { paused.add(v); v.pause(); setCtl(s, true); }
    });
  });
  // Pause when the gallery scrolls off-screen, resume when it returns.
  if ('IntersectionObserver' in window) {
    observe(new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const s = slides[index];
        const v = s && qs('video', s);
        if (!v) return;
        if (en.isIntersecting) { if (!paused.has(v) && !reduceMotion) v.play().catch(() => {}); }
        else v.pause();
      });
    }, { threshold: 0.15 })).observe(gallery);
  }
  syncVideo();

  qs('[data-is-prev]', gallery)?.addEventListener('click', () => show(index - 1));
  qs('[data-is-next]', gallery)?.addEventListener('click', () => show(index + 1));
  thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
  listen(document, 'is:tier', (e) => {
    const mi = parseInt(e.detail?.mediaIndex ?? '', 10);
    if (!Number.isNaN(mi) && mi >= 0 && mi < slides.length) show(mi);
  });

  // Swipe on touch devices
  const stage = qs('.is-gallery__stage', gallery);
  let startX = null;
  stage?.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
  stage?.addEventListener('touchend', (e) => {
    if (startX === null) return;
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) > 40) show(index + (dx < 0 ? 1 : -1));
    startX = null;
  });

  gallery.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') show(index - 1);
    if (e.key === 'ArrowRight') show(index + 1);
  });
}

/* ---------- What's in the box ---------- */

function initBox(root) {
  // Set pills (e.g. 14 / 20 piece): show that set's items + image right away, then switch the buy box
  // to the same set (which re-renders this section from the sibling's page).
  qsa('[data-is-box-set]', root).forEach((pill) =>
    listen(pill, 'click', () => {
      const id = pill.dataset.isBoxSet;
      qsa('[data-is-box-set]', root).forEach((p) => p.setAttribute('aria-selected', p === pill ? 'true' : 'false'));
      qsa('[data-set]', root).forEach((el) => (el.hidden = el.dataset.set !== id));
      const link = qs(`[data-is-buy] [data-is-set][data-pid="${id}"]`);
      if (link && link.getAttribute('aria-current') !== 'true') link.click();
    })
  );

  const tabs = qsa('[data-is-box-tab]', root);
  const qtyEls = qsa('[data-is-qty]', root);
  if (!tabs.length) return;

  const select = (i) => {
    const idx = Math.max(0, Math.min(tabs.length - 1, i));
    tabs.forEach((t, n) => t.setAttribute('aria-selected', n === idx ? 'true' : 'false'));
    const { m, l } = tabs[idx].dataset;
    qtyEls.forEach((el) => {
      el.textContent = (el.dataset.isQty || '').replace('{M}', m).replace('{L}', l);
    });
  };

  tabs.forEach((t, i) => t.addEventListener('click', () => select(i)));
  listen(document, 'is:tier', (e) => {
    const i = e.detail?.index;
    if (typeof i === 'number') select(i);
  });
  select(0);
}

/* ---------- Set switcher (sibling products, e.g. 14 / 20 piece) ---------- */
// Swaps the buy box (and sticky bar, box contents, product reviews) for the sibling product's
// without leaving the page. The sibling is rendered with the same template via ?view=.
function initSets(root) {
  qsa('[data-is-set]', root).forEach((a) =>
    listen(a, 'click', async (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
      e.preventDefault();
      if (a.getAttribute('aria-current') === 'true' || root.classList.contains('is-swapping')) return;
      root.classList.add('is-swapping');
      const oldPrice = qs('[data-is-price]', root)?.textContent.trim();
      try {
        const res = await fetch(a.dataset.isSet, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const next = doc.querySelector('[data-is-buy]');
        if (!next) throw new Error('no buy box');
        root.replaceWith(next);
        const swap = (sel) => {
          const from = doc.querySelector(sel);
          const to = qs(sel);
          if (from && to) to.replaceWith(from);
        };
        swap('[data-is-sticky]');
        ['box', 'reviews'].forEach((k) => swap(`[data-is-swap="${k}"]`));
        // Section CTAs ("Shop X — $price") follow the newly selected set.
        const newPrice = qs('[data-is-price]', next)?.textContent.trim();
        if (oldPrice && newPrice && oldPrice !== newPrice) {
          qsa('.is-cta .is-btn').forEach((b) =>
            b.childNodes.forEach((n) => {
              if (n.nodeType === 3 && n.textContent.includes(oldPrice)) n.textContent = n.textContent.replace(oldPrice, newPrice);
            })
          );
        }
        if (location.pathname.startsWith('/products/') && doc.title) document.title = doc.title;
        if (location.pathname.startsWith('/products/')) history.replaceState(history.state, '', a.getAttribute('href'));
        boot();
      } catch (err) {
        location.href = a.href;
      }
    })
  );
}

/* ---------- Sticky add-to-cart ---------- */

function initSticky(sticky, buy) {
  const btn = qs('[data-is-sticky-btn]', sticky);
  const labelEl = qs('[data-is-sticky-label]', sticky) || btn;
  const bagsEl = qs('[data-is-sticky-bags]', sticky);
  const priceEl = qs('[data-is-sticky-price]', sticky);
  const target = buy?.atcBtn;

  const sync = (d) => {
    if (bagsEl) bagsEl.textContent = d.bags;
    if (priceEl) priceEl.textContent = d.price;
    if (btn) {
      btn.disabled = d.placeholder || !d.available;
      // Only swap the label text so the cart icon survives (it is hidden via CSS when disabled).
      labelEl.textContent = d.placeholder ? 'Coming soon' : !d.available ? 'Sold out' : btn.dataset.label;
    }
  };

  if (btn) btn.dataset.label = labelEl.textContent.trim();

  listen(document, 'is:tier', (e) => sync(e.detail));
  // Initial sync from the selected tab
  const sel = qs('[data-is-tab][aria-selected="true"]');
  if (sel) {
    sync({
      bags: sel.dataset.bags,
      price: sel.dataset.price,
      placeholder: sel.dataset.placeholder === 'true',
      available: sel.dataset.available === 'true',
    });
  }

  btn?.addEventListener('click', () => {
    if (buy?.atcBtn && !buy.atcBtn.disabled) {
      buy.submit(null, qs('.is-sticky__img', sticky) || btn);
    } else {
      qs('#buy')?.scrollIntoView({ behavior: 'smooth' });
    }
  });

  if (!target || !('IntersectionObserver' in window)) {
    sticky.classList.add('is-visible');
    sticky.setAttribute('aria-hidden', 'false');
    return;
  }

  // Like Horizon: show once the buy button has scrolled above the viewport, hide again near the footer.
  let buyVisible = true;
  let footerVisible = false;
  const update = () => {
    const rect = target.getBoundingClientRect();
    const scrolledPast = !buyVisible && rect.top < 0;
    const show = scrolledPast && !footerVisible;
    sticky.classList.toggle('is-visible', show);
    sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    if (btn) btn.tabIndex = show ? 0 : -1;
  };
  observe(new IntersectionObserver(
    (entries) => {
      buyVisible = entries[0].isIntersecting;
      update();
    },
    { threshold: 0 }
  )).observe(target);
  const footer = qs('.is-footer') || qs('footer');
  if (footer) {
    observe(new IntersectionObserver(
      (entries) => {
        footerVisible = entries[0].isIntersecting;
        update();
      },
      { rootMargin: '200px 0px 0px 0px' }
    )).observe(footer);
  }
  listen(window, 'scroll', update, { passive: true });
}

/* ---------- Cross-sell quick add ---------- */

function initQuickAdd() {
  listen(document, 'click', async (e) => {
    const btn = e.target.closest?.('[data-is-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    const original = btn.textContent;
    btn.disabled = true;
    try {
      const cardImg = btn.closest('.is-pcard, .is-cross__card, [data-is-card], article, li')?.querySelector('img');
      await addToCart({
        source: btn,
        fly: { source: btn, image: btn.dataset.image || cardImg?.currentSrc || cardImg?.src || '', variant: 'quick' },
        items: [{ id: Number(btn.dataset.variantId), quantity: Number(btn.dataset.quantity || 1) }],
      });
    } catch (error) {
      btn.textContent = 'Try again';
      setTimeout(() => (btn.textContent = original), 2000);
    } finally {
      btn.disabled = false;
    }
  });
}

/* ---------- Reviews (Judge.me, best first) ---------- */

const JDGM_API = 'https://api.judge.me/reviews/reviews_for_widget';

function reviewsFromHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return qsa('.jdgm-rev', doc).map((r) => {
    const pic = qs('.jdgm-rev__pics img', r);
    const body = (qs('.jdgm-rev__body', r)?.textContent || '').trim();
    return {
      id: r.dataset.reviewId || '',
      score: parseInt(qs('.jdgm-rev__rating', r)?.dataset.score || '0', 10),
      author: (qs('.jdgm-rev__author', r)?.textContent || '').trim(),
      title: (qs('.jdgm-rev__title', r)?.textContent || '').trim(),
      body,
      words: body ? body.split(/\s+/).length : 0,
      verified: r.dataset.verifiedBuyer === 'true',
      date: qs('time', r)?.getAttribute('datetime') || '',
      photo: pic ? pic.dataset.src || pic.getAttribute('src') || '' : '',
      product: r.dataset.productTitle || '',
      productUrl: r.dataset.productUrl || '',
    };
  });
}

function renderReview(rv, showProduct = false) {
  const el = document.createElement('article');
  el.className = 'is-rev__card';
  const stars = '★'.repeat(rv.score);
  const when = rv.date ? new Date(rv.date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : '';
  el.innerHTML = `
    ${rv.photo ? `<img class="is-rev__photo is-rev__photo--img" loading="lazy" alt="">` : ''}
    ${rv.title ? `<b></b>` : ''}
    <span class="is-rev__stars" aria-label="${rv.score} star review">${stars}</span>
    <p></p>
    ${showProduct && rv.product ? `<a class="is-rev__product"></a>` : ''}
    <div class="is-rev__by"><b></b>${rv.verified ? '<span>✓ Verified buyer</span>' : ''}${when ? `<time class="is-rev__date"></time>` : ''}</div>`;
  if (showProduct && rv.product) { const a = qs('.is-rev__product', el); a.textContent = rv.product; if (rv.productUrl) a.href = rv.productUrl; }
  // text goes in via textContent so review content is never interpreted as HTML
  if (rv.photo) qs('img', el).src = rv.photo;
  if (rv.title) qs('b', el).textContent = rv.title;
  qs('p', el).textContent = rv.body;
  qs('.is-rev__by b', el).textContent = rv.author || 'Verified customer';
  if (when) qs('time', el).textContent = when;
  return el;
}

async function initReviews(root) {
  const list = qs('[data-is-reviews-list]', root);
  const d = root.dataset;
  const productIds = (d.productIds || d.productId || '').split(',').map((x) => x.trim()).filter(Boolean);
  if (!list || !productIds.length) return;
  const showProduct = d.showProduct === 'true';
  const perProduct = productIds.length > 1 ? 2 : Infinity;
  const count = parseInt(d.count || '6', 10);
  const minRating = parseInt(d.minRating || '4', 10);
  const minWords = parseInt(d.minWords || '8', 10);
  const split = (v) => (v || '').split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
  const pins = split(d.pins);
  const excludes = new Set(split(d.excludes));
  const preferPhotos = d.photos === 'true';
  const baseFor = (pid) => `${JDGM_API}?shop_domain=${encodeURIComponent(d.shop)}&platform=shopify&product_id=${encodeURIComponent(pid)}&per_page=10`;
  const cacheKey = `is-rev:${productIds.join('+')}:${count}:${minRating}:${minWords}:${pins.join('|')}:${preferPhotos}`;

  let picks = null;
  try {
    const c = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
    if (c && Date.now() - c.t < 3600e3) picks = c.r;
  } catch (e) { /* storage unavailable */ }

  if (!picks) {
    const urls = [];
    productIds.forEach((pid) => {
      const base = baseFor(pid);
      urls.push(`${base}&sort_by=rating&sort_dir=desc&page=1`);
      if (productIds.length === 1) urls.push(`${base}&sort_by=rating&sort_dir=desc&page=2`);
      if (preferPhotos) urls.push(`${base}&sort_by=pictures_first&page=1`);
    });
    let pool = [];
    try {
      const pages = await Promise.all(urls.map((u) => fetch(u).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      pages.forEach((pg) => { if (pg?.html) pool = pool.concat(reviewsFromHtml(pg.html)); });
    } catch (e) { return; }
    const seen = new Set();
    pool = pool.filter((rv) => rv.id && !seen.has(rv.id) && seen.add(rv.id));
    const rank = (rv) => {
      const pin = pins.indexOf(rv.id);
      return [pin === -1 ? 999 : pin, preferPhotos && rv.photo ? 0 : 1, -rv.score, -Math.min(rv.words, 60), rv.date ? -Date.parse(rv.date) : 0];
    };
    picks = pool
      .filter((rv) => !excludes.has(rv.id) && (pins.includes(rv.id) || (rv.score >= minRating && rv.words >= minWords)))
      .sort((a, b) => { const ra = rank(a), rb = rank(b); for (let i = 0; i < ra.length; i++) if (ra[i] !== rb[i]) return ra[i] - rb[i]; return 0; });
    // Store mode: no single product dominates the grid.
    const perCount = new Map();
    picks = picks.filter((rv) => { const n = (perCount.get(rv.product) || 0) + 1; perCount.set(rv.product, n); return n <= perProduct; }).slice(0, count);
    if (!picks.length) { qsa('.is-rev__card--skel', list).forEach((e) => e.remove()); return; }
    try { sessionStorage.setItem(cacheKey, JSON.stringify({ t: Date.now(), r: picks })); } catch (e) { /* ignore */ }
  }
  // Manual "Review" blocks are a bench: keep them after the live cards while live reviews are scarce.
  const minLive = parseInt(d.minLive || '3', 10);
  const manual = picks.length < minLive ? qsa('[data-is-manual]', list) : [];
  const live = picks.map((rv) => renderReview(rv, showProduct));
  list.replaceChildren(...live, ...manual.slice(0, Math.max(0, count - live.length)));
  root.classList.add('is-rev--live');
}

/* ---------- UGC: one clip at a time ---------- */

function initUgc() {
  // `play` doesn't bubble, so listen in the capture phase on the document. Covers every
  // .is-ugc grid on the page, and clips added later by the theme editor.
  listen(document, 
    'play',
    (e) => {
      const v = e.target;
      if (!(v instanceof HTMLVideoElement) || !v.closest('.is-ugc')) return;
      qsa('.is-ugc video').forEach((other) => {
        if (other !== v && !other.paused) other.pause();
      });
    },
    true
  );
}

/* ---------- Product card hover video ---------- */
// Horizon's card gallery shows the second slide on hover; when that slide is a product video
// (see snippets/card-gallery.liquid) play it while the pointer is over the card, reset on leave.
function initCardVideos() {
  const gallery = (e) => e.target.closest?.('.card-gallery');
  listen(document, 'pointerover', (e) => {
    if (e.pointerType !== 'mouse') return;
    const g = gallery(e);
    if (!g || g.contains(e.relatedTarget)) return;
    const v = g.querySelector('video.card-gallery__video');
    if (!v) return;
    v.preload = 'auto';
    v.muted = true;
    // Horizon's slideshow pauses media when it switches to the hover slide; start after that.
    clearTimeout(v._isPlay);
    v._isPlay = setTimeout(() => {
      if (g.matches(':hover')) v.play().catch(() => {});
    }, 120);
  });
  listen(document, 'pointerout', (e) => {
    const g = gallery(e);
    if (!g || g.contains(e.relatedTarget)) return;
    const v = g.querySelector('video.card-gallery__video');
    if (!v) return;
    clearTimeout(v._isPlay);
    v.pause();
    v.currentTime = 0;
  });
}

/* ---------- Boot ---------- */

function boot() {
  // Drop everything the previous boot bound (theme editor re-renders call boot() again).
  LIFE.abort();
  LIFE = new AbortController();
  OBSERVERS.splice(0).forEach((o) => o.disconnect());

  const buyRoot = qs('[data-is-buy]');
  const buy = buyRoot ? initBuyBox(buyRoot) : null;
  if (buyRoot) initSets(buyRoot);
  qsa('[data-is-box]').forEach(initBox);
  const sticky = qs('[data-is-sticky]');
  if (sticky) initSticky(sticky, buy);
  initQuickAdd();
  initCardVideos();
  qsa('[data-is-reviews]').forEach(initReviews);
  initUgc();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

// Theme editor: when a section is re-rendered, re-run boot() against the fresh DOM. boot() tears
// down the previous listeners/observers first, so nothing is double-bound and no reload is needed.
if (window.Shopify?.designMode) {
  let pending = null;
  const reboot = () => {
    clearTimeout(pending);
    pending = setTimeout(boot, 50);
  };
  ['shopify:section:load', 'shopify:section:reorder', 'shopify:block:select', 'shopify:block:deselect'].forEach((t) =>
    document.addEventListener(t, reboot)
  );
}

